import type React from "react";
import { Fragment, useEffect, useRef, useState } from "react";
import { describeReason } from "@vertu/shared";
import type { WidgetTenant } from "../types";
import type { WidgetAddon } from "../types";
import { setBookingAddons, type AddonSnapshotLine } from "../state/addons";
import { offersBookAnother, payBlockedByGuard } from "../state/charge-guard";

/** A special date's per-menu split as the hold stored it (20260925090010). */
type MenuSnapshotLine = {
  menu_id: string;
  menu_key: string;
  name: string;
  qty: number;
  unit_price_cents: number;
};
import { useCart, lineGrossCents, type CartItem } from "../state/cart";
import { editCartComboLine, evaluateCartCombos } from "../state/combos";
import { trackBeginCheckout, trackPurchase } from "../state/tracking";
import { pushHostBeginCheckout, pushHostPurchase } from "../state/host-datalayer";
import { linkPromoFor, shouldApplyLinkPromo, totalAfterLinkPromo } from "../state/link-promo";
import { useLinkPromoPreview, useLinkPromoRecheck } from "../state/link-promo-preview";
import {
  cancelCartBooking,
  getCartToken,
  startNewCart,
  updateBookingHold,
} from "../state/booking";
import { ParticipantDetails } from "./ParticipantDetails";
import { HoldTimer } from "./HoldTimer";
import { useVenueIdentityOr, VenueMark } from "./VenueMark";
import { useDrawerCoversHeader } from "./checkout/useCheckoutLayout";
import { fetchUnitWindowPrice } from "../state/unit-pricing";
import { formatLength } from "../state/availability-logic";
import { panelLineZone, tenantTz, ymdOfInstant, zonedInputMs, zonedInputValue } from "../state/tenant-time";
import { getBusyIntervals, findOverlap } from "../state/conflicts";
import { useCartHoldChips } from "../state/use-cart-hold-chips";
import type { HoldChip } from "../state/visit-checkout-logic";
import {
  computeCohostPairing,
  cohostNoticeText,
  type CohostPairing,
} from "../state/cohost";
import {
  checkoutLineCents,
  hopPayment,
  isChideskItem,
  useCheckoutSubmit,
  type CheckoutConfirmPayload,
} from "./checkout/useCheckoutSubmit";
import { formatDue, noticeNow, type ReopenedFields } from "../state/checkout-steps";
import type { CheckoutLayout, CheckoutLayoutSetting, CheckoutPresentation } from "@vertu/shared";
import type { CheckoutForm } from "../state/booking-questions-form";
import {
  asksPerPerson,
  decideCheckoutLayout,
  useCheckoutFormsProbe,
  usePhoneWidth,
} from "./checkout/useCheckoutLayout";
import { CheckoutPage } from "./checkout/CheckoutPage";
import {
  clearCheckoutResume,
  clearPaystackHop,
  saveCheckoutResume,
  type ResumedCheckout,
} from "./checkout/useCheckoutResume";
import {
  offersChooseAnotherTime,
  returnHint,
  returnMessage,
  returnOffer,
  type RefundKind,
  type ReturnPhase,
} from "../state/payment-return";
import {
  CHOOSE_ANOTHER_TIME,
  SUPERSEDED_PAY_BUSY,
  holdsToRelease,
  supersededWords,
  type PaystackReturnConfirmation,
} from "../state/confirmation-outcome";
import {
  CheckoutConsents,
  CheckoutTotals,
  ChideskAttribution,
  CohostNotice,
  ContactFields,
  PayNotes,
  SpecialRequestsField,
  payBlocked,
  GuardNotice,
} from "./checkout/CheckoutParts";

// An availability-panel line is shown on the venue's clock (the panel showed
// and held it there); every other line keeps the browser's clock, unchanged.
const venueZoneOf = panelLineZone;

type View = "cart" | "checkout" | "confirmed" | "returning";

/**
 * Back from Paystack with a payment reference (WidgetShell): the drawer says
 * "Confirming your payment…" while it asks the server and Paystack, then what
 * they said (state/payment-return.ts): paid ("Payment received — we're
 * confirming your booking…" until the webhook's confirmation is on file),
 * still going through, refunded (the booking could not be kept), or — with
 * no answer to trust — where the confirmation will arrive. Not paid reopens
 * the kept checkout instead. Never silent.
 */
export type PaymentReturnState = {
  phase: ReturnPhase;
  /** refunded: what the confirmation reader said (no booking, or the booking stands); absent on Paystack's word alone. */
  refund?: RefundKind | null;
  /** refunded: false when the refund on file is only part of the payment (never "in full"). */
  refundInFull?: boolean;
  /** received: the wait for the confirmation is over; it will arrive by email or WhatsApp. */
  late?: boolean;
  /** A Check again is on its way (the button says so). */
  checking?: boolean;
  onCheckAgain?: () => void;
  /** The checkout this tab kept (useCheckoutReturn), offered on "still". */
  onBackToCheckout?: () => void;
  /** not_paid: the kept checkout could not reopen by itself; the guest can try again. */
  onReviewAndPay?: () => void;
  /** Reopening it is under way. */
  backBusy?: boolean;
  /** Why it could not be reopened ("its hold has ended"). */
  problem?: string | null;
  /** superseded: the reader's answer (payment_superseded, 20261003200010). */
  superseded?: PaystackReturnConfirmation;
  /** superseded: open the venue's pay page for the updated total. */
  onPayUpdated?: () => void;
};

export interface ConfirmationSnapshot {
  customerFirstName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  bookingReference: string | null;
  /**
   * Amount of the in-widget (non-Paystack) confirmation, in cents — drives the
   * client-side `purchase` conversion. Null on the Paystack-return path, where
   * the value-accurate purchase is fired server-side (Phase 4) instead, so the
   * two layers never double-count one booking.
   */
  amountCents: number | null;
  /** Does the server report this booking to GA4 and Meta (CheckoutConfirmPayload)? Absent: true. */
  serverReports?: boolean;
  /**
   * Distinct service/brand names the guest just booked (captured before the
   * cart clears) so the success copy can lead with WHAT was booked — "Booking
   * at Stoep, Laborie Estate" — instead of only the tenant. Absent on the
   * Paystack-return path (cart context is gone), which falls back to tenant-
   * only copy.
   */
  serviceNames?: string[];
  /**
   * Issued QR tickets for an event-ticket purchase (Paystack-return path). When
   * present the confirmation shows a ticket-specific success state.
   */
  tickets?: ConfirmationTicket[];
  /** Absolute /manage/<token> URL that renders the QR tickets. */
  manageUrl?: string | null;
  /**
   * Advance-deposit deferral: a large group booked far ahead is reserved now
   * and pays the deposit later, so nothing was charged at checkout.
   */
  deferredDeposit?: boolean;
  depositDueAt?: string;
  depositCents?: number;
  depositCurrency?: string;
}

export interface ConfirmationTicket {
  qr_code: string;
  status: string;
  ticket_type: string;
  event_name: string;
  guest_name: string | null;
}

export function CartPanel({
  tenant,
  onClose,
  onConfirmedClose,
  onNavigateToService,
  onAddSpaTreatment,
  serviceAddons,
  initialView,
  initialConfirmation,
  checkoutLayouts,
  presentation,
  summaryImages,
  resume,
  paymentReturn,
}: {
  tenant: WidgetTenant;
  onClose: () => void;
  /**
   * Called by the confirmed-view "Done" button ONLY (never the backdrop / ×
   * / empty-cart close). Lets the shell redirect to a post-booking success
   * tab. Falls back to onClose when not provided.
   */
  onConfirmedClose?: () => void;
  /**
   * Co-hosting (Phase B): navigate the guest to a service's booking flow
   * (by slug) to satisfy a required-cohost ticket — the shell closes the
   * drawer and switches tabs. Optional: without it the gate still warns
   * and blocks, just without the "Add your {service} booking" button.
   */
  onNavigateToService?: (slug: string) => void;
  /** Keep this cart and return to its spa service to add another treatment. */
  onAddSpaTreatment?: (serviceId: string) => void;
  /** Per-service checkout add-on catalogue (serviceId → addons). */
  serviceAddons?: Record<string, WidgetAddon[]>;
  /**
   * Mount the drawer directly in this view. Used by the Paystack-return
   * handler to open straight into 'confirmed' with a pre-loaded snapshot
   * after a successful paid booking, skipping the cart/checkout steps.
   * Defaults to 'cart' for the normal user-opens-cart flow.
   */
  initialView?: View;
  /**
   * Pre-loaded confirmation data. Required when `initialView` is
   * 'confirmed' — the snapshot CheckoutView would normally capture is
   * passed in instead.
   */
  initialConfirmation?: ConfirmationSnapshot;
  /**
   * Each service's services.config.checkout_layout (serviceId → auto |
   * drawer | page): how its checkout opens. Absent: every service "auto".
   */
  checkoutLayouts?: Record<string, CheckoutLayoutSetting>;
  /** Widget-wide visual settings; omitted keeps the existing checkout. */
  presentation?: CheckoutPresentation;
  /** Vetted loaded-catalogue image URLs, keyed by the local cart line id. */
  summaryImages?: Record<string, string>;
  /** A full-page checkout restored after a reload or the Paystack round trip (initialView "checkout"). */
  resume?: ResumedCheckout | null;
  /** Back from Paystack, still confirming (initialView "returning"). */
  paymentReturn?: PaymentReturnState | null;
}) {
  const { items, subtotal, currency, remove, clear } = useCart();
  const [view, setView] = useState<View>(initialView ?? "cart");

  // ── Drawer or full page (checkoutLayout, owner 25 Sept) ────────────────
  // The cart's booking forms are read while "Your visit" is open, so the
  // decision is ready when the guest presses Continue; it is judged from the
  // forms' definitions and each service's setting, and frozen for as long as
  // the checkout is open, so the layout never changes while it is filled in.
  const formsSignature = items
    .filter((item) => !isChideskItem(item) && typeof item.metadata?.booking_id === "string")
    .map((item) => `${item.metadata.booking_id}:${item.metadata.party_size}`)
    .sort()
    .join("|");
  const probe = useCheckoutFormsProbe(getCartToken(), formsSignature, view === "cart" || view === "checkout");
  const phone = usePhoneWidth();
  // 640px or narrower the drawer is full-width and covers the widget's
  // header, so its top bar carries the same venue (as the full page's does).
  const coversHeader = useDrawerCoversHeader();
  const venue = useVenueIdentityOr(tenant);
  const [layout, setLayout] = useState<{ layout: CheckoutLayout; forms: CheckoutForm[] } | null>(null);
  // A restored checkout opens where the guest was, once: back to the cart
  // and in again starts at Your details.
  const resumeOnce = useRef(resume ?? null);
  const settingOf = (serviceId: string) => checkoutLayouts?.[serviceId] ?? "auto";
  const decided = (): { layout: CheckoutLayout; forms: CheckoutForm[] } | null =>
    probe.status === "ready"
      ? { layout: decideCheckoutLayout(probe.forms, settingOf, phone).layout, forms: probe.forms }
      : probe.status === "failed"
        ? { layout: "drawer", forms: [] }
        : null;
  useEffect(() => {
    if (view !== "checkout") {
      if (layout) setLayout(null);
      if (view === "cart") resumeOnce.current = null;
      return;
    }
    if (!layout) {
      const next = decided();
      if (next) setLayout(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, probe.status, layout]);

  // ── An offer the guest already qualifies for ──────────────────────────
  // Someone who books a Monday stay for one IS "Solo Stay — 50% off", and
  // without this pays full price and never learns it existed.
  //
  // The server decides everything: evaluate_cart_combos runs the same
  // resolve_combo_price the Packages tab runs, so a qualifying cart prices
  // identically whether the guest found the offer or the offer found them.
  // Nothing here computes a discount; it renders the per-leg amounts the
  // server wrote back.
  //
  // Failure is silent on purpose. A guest is never promised an offer they
  // have not been shown, so a lookup that times out costs nothing — whereas a
  // checkout that breaks because a discount lookup failed costs a booking.
  const { patchMetadata: patchComboMetadata } = useCart();
  const [autoCombo, setAutoCombo] = useState<{
    id: string;
    name: string;
    discountCents: number;
  } | null>(null);
  // Signature of what the server has already seen. Applying an offer patches
  // item metadata, which re-runs this effect; without the guard it would ask
  // again forever.
  const evaluatedRef = useRef<string>("");
  useEffect(() => {
    const priced = items
      .map((i) => (typeof i.metadata?.booking_id === "string" ? i.metadata.booking_id : null))
      .filter((b): b is string => b !== null)
      .sort();
    if (priced.length === 0) {
      evaluatedRef.current = "";
      setAutoCombo(null);
      return;
    }
    const signature = priced.join("|");
    if (evaluatedRef.current === signature) return;

    // Debounced: adding three things to a cart is three renders, and each
    // evaluation is a pricing volley on the server.
    let stale = false;
    const timer = window.setTimeout(() => {
      evaluateCartCombos(getCartToken()).then((res) => {
        if (stale) return;
        evaluatedRef.current = signature;
        if (!res.ok || !res.applied || !res.combo_id) return;
        setAutoCombo({
          id: res.combo_id,
          name: res.combo_name ?? "Offer",
          discountCents: res.discount_cents ?? 0,
        });
        // Show what the server actually wrote to each leg.
        for (const alloc of res.allocations ?? []) {
          const line = items.find((i) => i.metadata?.booking_id === alloc.ref);
          if (line) {
            patchComboMetadata(line.id, {
              amount_cents: alloc.amount_cents,
              combo_id: res.combo_id,
              combo_auto: true,
            });
          }
        }
      });
    }, 350);
    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
  }, [items, patchComboMetadata]);

  // Removing a line must also CANCEL its pending hold — checkout charges
  // every pending booking under the cart_token, so a client-only removal
  // would still get booked and paid for. Combo lines travel as a package:
  // removing one removes (and cancels) every line of that combo, since the
  // remaining components carry a bundle-discounted split that no longer
  // applies on its own.
  function removeLine(id: string) {
    const target = items.find((i) => i.id === id);
    if (!target) return;
    const comboId = target.metadata?.combo_id;
    const doomed = comboId
      ? items.filter((i) => i.metadata?.combo_id === comboId)
      : [target];
    for (const item of doomed) {
      const bookingId = item.metadata?.booking_id;
      if (typeof bookingId === "string") {
        void cancelCartBooking(bookingId); // best-effort; expired holds no-op
      }
      remove(item.id);
    }
  }
  // Choose another time, after a refund with no booking (20261001160010): a
  // fresh cart. The refunded payment stays with this cart token, and the
  // webhook's double-charge guard keeps a cart's first payment and refunds
  // any later one, so a new time paid for on the same token would be
  // refunded too. Every line's hold is let go under the old token (the
  // cancel reads the token as it is called), the lines leave, what this tab
  // kept for the trip is forgotten, the tab takes a new token, and the
  // drawer (or the page) closes onto the services.
  function chooseAnotherTime() {
    for (const bookingId of holdsToRelease(items)) {
      void cancelCartBooking(bookingId); // best-effort; expired holds no-op
    }
    clear();
    clearCheckoutResume(tenant.slug);
    clearPaystackHop(tenant.slug);
    startNewCart();
    onClose();
  }
  // Snapshot of what the guest just booked + how to reach them. Captured
  // when CheckoutView fires onConfirm so ConfirmedView can render rich
  // success state (name in the heading, booking reference, channel-aware
  // confirmation copy) without depending on the cart, which clears.
  const [confirmation, setConfirmation] = useState<ConfirmationSnapshot>(
    initialConfirmation ?? {
      customerFirstName: null,
      customerEmail: null,
      customerPhone: null,
      bookingReference: null,
      amountCents: null,
    },
  );

  function confirmCheckout(payload?: CheckoutConfirmPayload) {
    setConfirmation({
      customerFirstName: payload?.customerFirstName ?? null,
      customerEmail: payload?.customerEmail ?? null,
      customerPhone: payload?.customerPhone ?? null,
      bookingReference: payload?.bookingReference ?? null,
      amountCents: payload?.amountCents ?? null,
      serverReports: payload?.serverReports,
      // Captured NOW — the cart clears on the next line.
      serviceNames: [
        ...new Set(items.map((i) => i.serviceName).filter(Boolean)),
      ],
      deferredDeposit: payload?.deferredDeposit,
      depositDueAt: payload?.depositDueAt,
      depositCents: payload?.depositCents,
      depositCurrency: payload?.depositCurrency,
    });
    clear();
    setView("confirmed");
  }

  // The × and a click outside. Leaving the drawer's checkout forgets the
  // checkout this tab kept for the trip to Paystack, as the full page's ×
  // does, so it never reopens by itself on the next page; its lines stay in
  // the cart. So does leaving "We haven't received your payment" (Paystack
  // said so: nothing is left to ask about). The views that are still asking
  // or that say the money is in (confirming, received, going, still) keep
  // remembering the trip, so a reload still asks Paystack.
  function close() {
    if (view === "checkout") clearCheckoutResume(tenant.slug);
    if (view === "returning" && paymentReturn?.phase === "not_paid") {
      clearCheckoutResume(tenant.slug);
      clearPaystackHop(tenant.slug);
    }
    onClose();
  }

  // The full page covers the widget instead of the drawer.
  if (view === "checkout" && layout?.layout === "page") {
    return (
      <CheckoutPage
        tenant={tenant}
        items={items}
        currency={currency}
        forms={layout.forms}
        phone={phone}
        presentation={presentation}
        summaryImages={summaryImages}
        resume={resumeOnce.current}
        onBack={() => setView("cart")}
        onClose={onClose}
        onChooseAnotherTime={chooseAnotherTime}
        onConfirm={confirmCheckout}
      />
    );
  }

  const titles = { cart: "Your visit", checkout: "Checkout", returning: "Payment", confirmed: "Confirmed" };
  const title = titles[view];
  const countText = `${items.length} ${items.length === 1 ? "item" : "items"}`;

  return (
    <div className="vt-cart-backdrop" onClick={close}>
      <aside
        className="vt-cart-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={coversHeader ? `${title} — ${venue.title}` : undefined}
        data-covers-header={coversHeader ? "true" : undefined}
      >
        {/* Full-width (640px or narrower), the drawer covers the widget's
            header, so its top bar is the full page's: the header's look and
            venue, with the drawer's title ("Your visit", "Checkout"…) beside
            the ×, still the bar's only control. Wider (a laptop, a phone held
            sideways, a short window) the drawer is a side panel beside the
            header: its own bar, as before. The bar stays .vt-cart-header (the
            drawer's header as the rest of the widget finds it), and both keep
            the same elements where they overlap, so the × is one element that
            keeps its focus as the window crosses 640px. */}
        <div className={coversHeader ? "vt-cart-header vt-header vt-checkout-page-bar" : "vt-cart-header"}>
          {coversHeader && <VenueMark identity={venue} titleAs="div" decorative taglineOneLineOrNone />}
          <div className="vt-cart-header-text">
            <h3 className={coversHeader ? "vt-checkout-page-title" : undefined}>{title}</h3>
            {coversHeader &&
              // The label keeps one width in every view (the widest title, or
              // the count), so the venue's column, and with it the tagline and
              // the name's lines, never changes as the guest moves on. Never
              // seen or read: the words are the CSS's (attr), not the page's.
              [...Object.values(titles), countText].map((text) => (
                <span key={text} className="vt-checkout-page-title vt-cart-title-sizer" data-text={text} aria-hidden="true" />
              ))}
            {view === "cart" && (
              <p className="vt-cart-header-count">
                {items.length} {items.length === 1 ? "item" : "items"}
              </p>
            )}
          </div>
          <button type="button" className="vt-cart-close" onClick={close} aria-label="Close">
            ×
          </button>
        </div>

        {view === "cart" && (
          <CartView
            items={items}
            subtotal={subtotal}
            currency={currency}
            autoCombo={autoCombo}
            linkPromo={linkPromoFor(tenant.slug)}
            onRemove={removeLine}
            serviceAddons={serviceAddons}
            onNavigateToService={onNavigateToService}
            onAddSpaTreatment={onAddSpaTreatment}
            onCheckout={() => {
              trackBeginCheckout(subtotal, currency);
              pushHostBeginCheckout({ tenant: tenant.slug, value: subtotal, currency });
              // Decided now when the forms are in (the usual case: they are
              // read while this view is open), so the page never shows the
              // drawer first.
              const next = decided();
              if (next) setLayout(next);
              setView("checkout");
            }}
          />
        )}
        {view === "checkout" && !layout && (
          <div className="vt-checkout vt-cart-checkout">
            <button type="button" className="vt-link" onClick={() => setView("cart")}>
              ← Back to your visit
            </button>
            <p className="vt-checkout-note" role="status">
              Getting your checkout ready…
            </p>
          </div>
        )}
        {view === "checkout" && layout && (
          <CheckoutView
            tenant={tenant}
            items={items}
            subtotal={subtotal}
            currency={currency}
            // A phone pages the riders when the forms ask per person — and
            // when the forms were too slow to read before the checkout
            // opened (forms: []): a weak signal is where saving each rider
            // as the guest goes matters most. A form that asks nothing per
            // person shows no pager either way.
            paged={phone && (layout.forms.length === 0 || asksPerPerson(layout.forms))}
            notice={resumeOnce.current?.notice ?? null}
            resumed={resumeOnce.current ?? null}
            reopened={resumeOnce.current?.fields ?? null}
            onBack={() => {
              // The checkout the drawer kept for the Paystack trip is left.
              clearCheckoutResume(tenant.slug);
              setView("cart");
            }}
            onChooseAnotherTime={chooseAnotherTime}
            onConfirm={confirmCheckout}
          />
        )}
        {view === "returning" && (
          <PaymentReturnView
            state={paymentReturn ?? null}
            onClose={close}
            onChooseAnotherTime={chooseAnotherTime}
            // Only the superseded view says a time (how long the booking is
            // held); tenantTz builds an Intl formatter, which is not free on
            // a slow phone, so the other views never pay for it.
            timeZone={paymentReturn?.phase === "superseded" ? tenantTz(tenant) : undefined}
          />
        )}
        {view === "confirmed" && (
          <ConfirmedView
            tenant={tenant}
            confirmation={confirmation}
            onClose={onConfirmedClose ?? onClose}
          />
        )}
      </aside>
    </div>
  );
}

function CartView({
  items,
  subtotal,
  currency,
  onRemove,
  onNavigateToService,
  onAddSpaTreatment,
  serviceAddons,
  onCheckout,
  autoCombo,
  linkPromo = null,
}: {
  items: ReturnType<typeof useCart>["items"];
  subtotal: number;
  currency: string;
  /** An offer the cart qualified for and the server attached. */
  autoCombo?: { id: string; name: string; discountCents: number } | null;
  /** The promo code the booking link carried (?promo=), applied at checkout. */
  linkPromo?: string | null;
  onRemove: (id: string) => void;
  onNavigateToService?: (slug: string) => void;
  onAddSpaTreatment?: (serviceId: string) => void;
  serviceAddons?: Record<string, WidgetAddon[]>;
  onCheckout: () => void;
}) {
  // Each line's "Held for N min while you check out" chip, from the server's
  // own hold expiry (never an assumed 15 minutes).
  const holdChip = useCartHoldChips(items);
  // The link's code once checked (link-promo-preview.tsx), asked again each
  // time the drawer opens: it may have expired or run out since the hold.
  const linkPreview = useLinkPromoPreview();
  const recheckLinkPromo = useLinkPromoRecheck();
  useEffect(() => {
    recheckLinkPromo();
  }, [recheckLinkPromo]);
  if (items.length === 0) {
    return (
      <div className="vt-cart-empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4H6z" />
          <path d="M3 6h18M16 10a4 4 0 01-8 0" />
        </svg>
        <p>Your visit is empty</p>
        <p className="vt-muted">Build it up by adding services, treatments, or events.</p>
      </div>
    );
  }

  // Conflict detection — flag items whose time windows overlap so the
  // customer can't accidentally book to be in two places at once.
  const conflictsByItemId = computeTimeConflicts(items);

  // Co-hosting (Phase B): pair requiring tickets 1:1 with sibling
  // bookings of the required service — paired bundled/free_reserve
  // siblings display as Included/Free and drop out of the pay-now sum
  // (mirroring the server, which zeroes their real amount); an unpaired
  // REQUIRED ticket gates the checkout CTA below.
  const cohostPairing = computeCohostPairing(items);
  const cohostUnmet = cohostPairing.unmet[0] ?? null;

  // Two-tier total — what's charged now (full payments + deposits) vs
  // what's settled at the venue (deposit balances + restaurant bills).
  const totals = computeCartTotals(items, cohostPairing);
  // The checkout applies a link's promo code on its own rule
  // (shouldApplyLinkPromo): codes taken (no spa treatments, a booking held)
  // and something charged now, by the checkout's own per-line amounts — not
  // the visit's prices above, which a R0 hold (a table with no deposit) does
  // not charge.
  // Once checked (link-promo-preview.tsx): the total after the code, struck
  // through before it, only where the drawer's total is what the checkout
  // charges now.
  const payNowAfter = totalAfterLinkPromo(totals.payNowCents, linkPreview, { fullPriceOnly: false });
  const vertuLines = items.filter((item) => !isChideskItem(item));
  const showLinkPromo = shouldApplyLinkPromo({
    code: linkPromo,
    codesAccepted:
      vertuLines.length === items.length &&
      vertuLines.some((item) => typeof item.metadata?.booking_id === "string"),
    chargedNowCents: vertuLines.reduce((n, item) => n + checkoutLineCents(item, cohostPairing.siblingModes), 0),
    alreadyTried: false,
    appliedCount: 0,
  });
  const spaItems = items.filter(isChideskItem);
  const latestSpaServiceId = spaItems[spaItems.length - 1]?.serviceId;

  // Fallback subtotal (shown when nothing is due now): exclude zeroed
  // cohost siblings so it can't quote a price the server won't charge.
  // (Same currency units as `subtotal` — unitPrice is in rands.)
  const zeroedSubtotal = items.reduce((sum, it) => {
    const m = cohostPairing.siblingModes.get(it.id);
    return m === "bundled" || m === "free_reserve"
      ? sum + (it.unitPrice ?? 0) * it.quantity
      : sum;
  }, 0);
  const displaySubtotal = Math.max(0, subtotal - zeroedSubtotal);

  // Group split lines into "visits" — same service + same slot with 2+ items
  // (a party that picked several tastings together) — so they read as ONE
  // visit of N guests instead of separate 2-guest bookings. Accommodation
  // keeps its own per-room lines (own nights/room semantics).
  const visitKeyOf = (it: CartItem) =>
    it.serviceType === "accommodation"
      ? `solo:${it.id}`
      : `${it.serviceId}|${String((it.metadata ?? {}).starts_at ?? "")}`;
  const membersByKey = new Map<string, CartItem[]>();
  const keyOrder: string[] = [];
  for (const it of items) {
    const k = visitKeyOf(it);
    const arr = membersByKey.get(k);
    if (arr) arr.push(it);
    else {
      membersByKey.set(k, [it]);
      keyOrder.push(k);
    }
  }
  // Members of a visit rendered contiguously, with a header <li> before the
  // first. groupInfo tells each line whether it's grouped (→ suppress its
  // When/Guests chips) and whether it leads a group (→ render the header).
  const orderedItems = keyOrder.flatMap((k) => membersByKey.get(k)!);
  const groupInfo = new Map<
    string,
    { grouped: boolean; header: { guests: number; whenIso: string | null } | null }
  >();
  for (const k of keyOrder) {
    const members = membersByKey.get(k)!;
    if (members.length < 2) {
      groupInfo.set(members[0]!.id, { grouped: false, header: null });
      continue;
    }
    const md0 = members[0]!.metadata ?? {};
    const guests =
      typeof md0.visit_guests === "number" && md0.visit_guests > 0
        ? md0.visit_guests
        : members.reduce((n, m) => {
            const p = (m.metadata ?? {}).party_size;
            return n + (typeof p === "number" ? p : 0);
          }, 0);
    const whenIso = typeof md0.starts_at === "string" ? md0.starts_at : null;
    members.forEach((m, i) => {
      groupInfo.set(m.id, {
        grouped: true,
        header: i === 0 ? { guests, whenIso } : null,
      });
    });
  }

  return (
    <>
      {autoCombo && autoCombo.discountCents > 0 && (
        <div className="vt-cart-offer" role="status">
          <strong>{autoCombo.name}</strong> applied — you saved{" "}
          {currency} {(autoCombo.discountCents / 100).toLocaleString()}
        </div>
      )}
      <ul className="vt-cart-items">
        {orderedItems.map((item) => {
          const gi = groupInfo.get(item.id) ?? { grouped: false, header: null };
          const conflict = conflictsByItemId.get(item.id) ?? null;
          // Accommodation shows the whole-stay total (rate x nights, computed
          // server-side into amount_cents) with a "R x x N nights" breakdown,
          // instead of unitPrice x quantity - which is a single night.
          const md = item.metadata ?? {};
          const isAccom = item.serviceType === "accommodation";
          const accomAmount =
            isAccom && typeof md.amount_cents === "number" && md.amount_cents > 0
              ? md.amount_cents
              : null;
          const accomNights =
            isAccom &&
            typeof md.starts_at === "string" &&
            typeof md.ends_at === "string"
              ? nightsBetween(md.starts_at, md.ends_at)
              : null;
          // Cohost sibling paired under bundled/free_reserve: the server
          // zeroes its real amount, so show what the guest actually pays
          // instead of the list price (display-level only).
          const cohostMode = cohostPairing.siblingModes.get(item.id) ?? null;
          const cohostPriceLabel =
            cohostMode === "bundled"
              ? "Included"
              : cohostMode === "free_reserve"
                ? "Free — pay on arrival"
                : null;
          return (
            <Fragment key={item.id}>
              {gi.header && (
                <li className="vt-cart-visit-header">
                  <span className="vt-cart-visit-title">Your visit</span>
                  <span className="vt-cart-visit-meta">
                    {gi.header.guests > 0
                      ? `${gi.header.guests} guest${gi.header.guests === 1 ? "" : "s"}`
                      : ""}
                    {gi.header.guests > 0 && gi.header.whenIso ? " · " : ""}
                    {gi.header.whenIso
                      ? formatChipDateTime(gi.header.whenIso, isChideskItem(item), chipTzOffset(item.metadata), venueZoneOf(item.metadata))
                      : ""}
                  </span>
                </li>
              )}
              <li
                className={`vt-cart-item${gi.grouped ? " vt-cart-item-grouped" : ""}`}
              >
              <div className="vt-cart-item-info">
                {item.brandName && (
                  <span className="vt-cart-item-brand">{item.brandName}</span>
                )}
                <div className="vt-cart-item-name">
                  {item.description}
                  {item.quantity > 1 && (
                    <span className="vt-cart-item-qty">
                      {/* Spa treatments are per person — "for N guests" reads
                          as headcount, where "× N" read as a double-booking. */}
                      {item.metadata?.source === "chidesk"
                        ? ` · for ${item.quantity} guests`
                        : ` · × ${item.quantity}`}
                    </span>
                  )}
                </div>
                {Array.isArray(item.metadata.special_date_menu_lines) &&
                  (item.metadata.special_date_menu_lines as MenuSnapshotLine[]).length > 0 && (
                    <div className="vt-cart-addon-lines">
                      {(item.metadata.special_date_menu_lines as MenuSnapshotLine[]).map((l) => (
                        <div key={l.menu_id} className="vt-cart-addon-line">
                          {l.qty}× {l.name} —{" "}
                          {item.currency} {((l.unit_price_cents * l.qty) / 100).toLocaleString()}
                        </div>
                      ))}
                    </div>
                  )}
                {Array.isArray(item.metadata.addons) &&
                  (item.metadata.addons as AddonSnapshotLine[]).length > 0 && (
                    <div className="vt-cart-addon-lines">
                      {(item.metadata.addons as AddonSnapshotLine[]).map((a) => (
                        <div key={a.addon_id} className="vt-cart-addon-line">
                          + {a.qty}× {a.name} —{" "}
                          {a.currency} {((a.unit_price_cents * a.qty) / 100).toLocaleString()}
                        </div>
                      ))}
                    </div>
                  )}
                {describeSeat(item.metadata, item.serviceType, holdChip)}
                <CartItemChips
                  item={item}
                  grouped={gi.grouped}
                  cohostMode={cohostMode}
                />
                {conflict && (
                  /* The ⚠ comes from .vt-cart-item-warning::before — a literal
                     one here too rendered TWO. And the box is display:flex, so
                     every bare text node and <strong> became its own flex item
                     and the sentence broke across lines mid-phrase; the span
                     keeps it as one item that wraps normally. */
                  <div className="vt-cart-item-warning" role="alert">
                    <span>
                      {md.source === "chidesk" ? (
                        <>
                          Same time as <strong>{conflict}</strong> — one guest
                          can&apos;t do both at once. Change the time, or book one
                          for someone else at checkout.
                        </>
                      ) : (
                        <>
                          Overlaps with <strong>{conflict}</strong> — you
                          can&apos;t be in two places at once.
                        </>
                      )}
                    </span>
                  </div>
                )}
              </div>
              <div className="vt-cart-item-right">
                {cohostPriceLabel ? (
                  <div className="vt-cart-item-price vt-cart-item-price-cohost">
                    {cohostPriceLabel}
                  </div>
                ) : accomAmount != null ? (
                  <div className="vt-cart-item-price">
                    {item.currency}{" "}
                    {(accomAmount / 100).toLocaleString()}
                    {accomNights != null && accomNights > 0 && (
                      <span className="vt-cart-item-price-sub">
                        {/* Per-night derived from the total actually charged,
                            not item.unitPrice — that is the display price and
                            can be the stale base rate. Across several nights
                            at different Cloudbeds rates this is an average,
                            which is why it is labelled one. */}
                        {item.currency}{" "}
                        {Math.round(accomAmount / accomNights / 100).toLocaleString()}{" "}
                        {accomNights === 1 ? "" : "avg "}× {accomNights} night
                        {accomNights === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                ) : item.unitPrice != null ? (
                  <div className="vt-cart-item-price">
                    {item.currency}{" "}
                    {(item.unitPrice * item.quantity).toLocaleString()}
                  </div>
                ) : (
                  <div className="vt-cart-item-price vt-cart-item-price-placeholder" aria-hidden />
                )}
                <button
                  type="button"
                  className="vt-cart-item-remove"
                  onClick={() => onRemove(item.id)}
                >
                  Remove
                </button>
              </div>
              </li>
            </Fragment>
          );
        })}
      </ul>
      <AddonStrip items={items} serviceAddons={serviceAddons} />
      <div className="vt-cart-footer">
        <div className="vt-cart-total">
          <span>{totals.payNowLabel}</span>
          <span className="vt-cart-total-amount">
            {payNowAfter !== null ? (
              <>
                <s className="vt-promo-was">
                  <span className="vt-sr-only">Was </span>
                  {formatDue(currency, totals.payNowCents)}
                </s>{" "}
                {formatDue(currency, payNowAfter)}
              </>
            ) : totals.payNowCents > 0
              ? `${currency} ${(totals.payNowCents / 100).toLocaleString()}`
              : totals.hasReservation
                ? "At venue"
                : `${currency} ${displaySubtotal.toLocaleString()}`}
          </span>
        </div>
        {/* A campaign link's promo code: the total above is before it. The
            checkout shows what it takes off, or why it cannot. */}
        {payNowAfter !== null && linkPreview ? (
          <div className="vt-cart-subtotal vt-cart-promo-note" data-kind="off">
            <span>Discount · {linkPreview.code} (from your link)</span>
            <span>−{formatDue(currency, linkPreview.discountCents)}</span>
          </div>
        ) : (
          showLinkPromo && (
            <div className="vt-cart-subtotal vt-cart-promo-note">
              <span>Promo code {linkPromo} from your link</span>
              <span>Shown at checkout</span>
            </div>
          )
        )}
        {totals.payLaterCents > 0 && (
          <div className="vt-cart-subtotal">
            <span>Then at the venue</span>
            <span>
              {currency} {(totals.payLaterCents / 100).toLocaleString()}
            </span>
          </div>
        )}
        {totals.hasReservation && totals.payNowCents > 0 && totals.payLaterCents === 0 && (
          <div className="vt-cart-subtotal">
            <span>Plus restaurant bill at the table</span>
            <span>—</span>
          </div>
        )}
        {/* Co-hosting gate (client-side mirror — the server enforces the
            real block): a required-cohost ticket with no qualifying
            sibling booking blocks the CTA until the booking is added. */}
        {cohostUnmet && (
          <div className="vt-cart-cohost" role="alert">
            <p className="vt-cart-cohost-text">
              {cohostNoticeText(cohostUnmet.cohost, cohostUnmet.eventName)}
            </p>
            {onNavigateToService && (
              <button
                type="button"
                className="vt-cta-inline"
                onClick={() =>
                  onNavigateToService(
                    cohostUnmet.cohost.required_service_slug,
                  )
                }
              >
                Add your {cohostUnmet.cohost.required_service_name} booking
              </button>
            )}
          </div>
        )}
        <button type="button" className="vt-cta" onClick={onCheckout}>
          {totals.ctaLabel}
        </button>
        {latestSpaServiceId && onAddSpaTreatment && (
          <div>
            <button
              type="button"
              className="vt-link"
              onClick={() => onAddSpaTreatment(latestSpaServiceId)}
            >
              + Add another treatment
            </button>
            <p className="vt-muted" style={{ fontSize: 12 }}>
              Add treatments for yourself or another guest, then check out together.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Chip rail rendered below each cart item: when/duration, party,
 * seat/table label, special-date badge, and a payment-policy pill
 * derived from item metadata. Mirrors the prototype's cart-item visual
 * vocabulary against the production item.metadata shape (which already
 * carries starts_at / party_size / amount_cents / requires_full_payment /
 * special_date_event_name from the booking-hold flow).
 */
function CartItemChips({
  item,
  grouped = false,
  cohostMode = null,
}: {
  item: CartItem;
  // Inside a "visit" group the shared When + Guests are shown on the group
  // header, so suppress the per-line When/Guests chips here to avoid the
  // "two separate 2-guest bookings" read. Line-specific chips (payment policy,
  // seat, special) stay.
  grouped?: boolean;
  // Cohost pairing (Phase B): when this line is the sibling satisfying a
  // bundled/free_reserve ticket requirement, the server zeroes its charge —
  // override the payment-policy chip so it can't claim "Pay R X now".
  cohostMode?: "separate_paid" | "bundled" | "free_reserve" | null;
}) {
  const { patchMetadata, updateQty, updatePrice } = useCart();
  // The link's code comes off the visit's total (the drawer's footer says
  // what): a line's own "Pay ZAR 5,600 now" would contradict it.
  const linkPreview = useLinkPromoPreview();
  const md = item.metadata ?? {};
  // Per-person services price by head — editing the guests stepper must also
  // move the priced quantity so the line total tracks the party size. Per-unit
  // services leave quantity alone (guests stay informational).
  const perPerson = md.pricing_basis === "per_person";
  // A deposit tracked separately from amount_cents (e.g. a ChiDesk spa
  // treatment whose full price settles at the venue). Surfaced on the line so
  // the customer isn't told "no charge to reserve" when a deposit is in fact due.
  const depositCents =
    typeof md.deposit_cents === "number" ? md.deposit_cents : null;
  const startsAt = typeof md.starts_at === "string" ? md.starts_at : null;
  const endsAt = typeof md.ends_at === "string" ? md.ends_at : null;
  const venueZone = venueZoneOf(md);
  const partySize =
    typeof md.party_size === "number" && md.party_size > 0
      ? md.party_size
      : null;
  const seatLabel = typeof md.seat_label === "string" ? md.seat_label : null;
  const specialName =
    typeof md.special_date_event_name === "string"
      ? md.special_date_event_name
      : null;
  const requiresFull = md.requires_full_payment === true;
  const amountCents =
    typeof md.amount_cents === "number" ? md.amount_cents : null;
  const lineTotalCents = lineGrossCents(item);

  // Accommodation: cap the in-cart guests stepper at the room's capacity so a
  // visitor can't push a room past what it sleeps without adding another room.
  // (unit_capacity is stamped on the cart row when the room is added.)
  const isAccommodation = item.serviceType === "accommodation";
  const roomCapacity =
    isAccommodation && typeof md.unit_capacity === "number" && md.unit_capacity > 0
      ? md.unit_capacity
      : null;
  const guestsMax = roomCapacity ?? 50;
  // A per-person option's fewest guests per booking (service_units.
  // party_size_min, stamped on the line when it was added): the hold refuses
  // a party below it, so the stepper stops there.
  const guestsMin =
    perPerson && typeof md.party_size_min === "number" && md.party_size_min >= 2
      ? Math.floor(md.party_size_min)
      : 1;
  const atRoomCapacity =
    roomCapacity != null && partySize != null && partySize >= roomCapacity;

  // Server-backed editing of the held booking. Only Vertu bookings have
  // a booking_id (ChiDesk items track their own appointment id, fixed
  // events use a different flow). Special-date events are pinned to
  // their menu time and party-size options — out of scope for inline
  // edit. Pure reservation items (no booking_id) get static chips.
  const bookingId =
    typeof md.booking_id === "string" ? md.booking_id : null;
  // Combo lines are NOT hold-editable: update_booking_hold re-prices at
  // standard service rates with no combo awareness, which would silently
  // destroy the bundle-discounted allocation written by create_combo_hold.
  const isEditable =
    bookingId != null &&
    md.source !== "chidesk" &&
    md.source !== "combo" &&
    !specialName;

  const [editError, setEditError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  // What this change would cost them, held until they say yes. The rule is
  // that a discount is never taken away silently — this is the "silently".
  const [losingOffer, setLosingOffer] = useState<{
    name: string;
    cents: number;
    patch: { starts_at: string; ends_at: string; party_size: number };
  } | null>(null);
  const [comboEditConfirmed, setComboEditConfirmed] = useState(false);

  /**
   * Calls update_booking_hold with the new window/party. On success,
   * mirrors the change back to local cart metadata so the chip rail
   * re-renders. On failure, surfaces a short inline error and leaves
   * the original values intact.
   */
  async function applyEdit(patch: {
    starts_at?: string;
    ends_at?: string;
    party_size?: number;
  }) {
    if (!bookingId || !startsAt || !endsAt) return;
    const nextStarts = patch.starts_at ?? startsAt;
    const nextEnds = patch.ends_at ?? endsAt;
    const nextParty = patch.party_size ?? partySize ?? 1;
    setEditError(null);
    setEditing(true);
    try {
      // A line an offer attached itself to cannot go through the ordinary
      // reprice — update_booking_hold refuses it, because repricing one leg
      // from its own rate would destroy the bundle split. It goes through the
      // combo editor instead, which detaches, reprices and re-evaluates in one
      // transaction, and asks first if the change costs the guest the offer.
      if (md.combo_auto === true) {
        const probe = await editCartComboLine({
          cartToken: getCartToken(),
          bookingId,
          startsAt: nextStarts,
          endsAt: nextEnds,
          partySize: nextParty,
          confirmed: comboEditConfirmed,
        });
        if (probe.ok && probe.reason === "would_lose_offer") {
          // Never taken away silently. The guest is told what it costs and
          // decides; nothing has changed at this point.
          setLosingOffer({
            name: probe.offer_name ?? "your offer",
            cents: probe.discount_cents ?? 0,
            patch: { starts_at: nextStarts, ends_at: nextEnds, party_size: nextParty },
          });
          setEditing(false);
          return;
        }
        if (!probe.ok) {
          setEditError(
            probe.reason === "no_combo"
              ? "Couldn't change that — try again."
              : (probe.reason ?? "Couldn't change that."),
          );
          setEditing(false);
          return;
        }
        // Mirror the server's new state onto the line: the window the guest
        // asked for, and whatever the re-evaluation decided each leg now
        // costs. combo_auto tracks whether the offer survived, so the next
        // edit takes the right path.
        const alloc = probe.result?.allocations?.find((a) => a.ref === bookingId);
        patchMetadata(item.id, {
          starts_at: nextStarts,
          ends_at: nextEnds,
          party_size: nextParty,
          ...(alloc ? { amount_cents: alloc.amount_cents } : {}),
          combo_auto: probe.kept_offer === true,
          ...(probe.kept_offer === true ? {} : { combo_id: null }),
        });
        if (perPerson) updateQty(item.id, nextParty);
        setComboEditConfirmed(false);
        setLosingOffer(null);
        setEditing(false);
        return;
      }

      const result = await updateBookingHold({
        bookingId,
        startsAt: nextStarts,
        endsAt: nextEnds,
        partySize: nextParty,
      });
      if (!result.ok) {
        // update_booking_hold (PR #76 migration) now mirrors every gate
        // create_booking_hold runs, so we can hit oversize-party / rules /
        // operating-hours rejections in addition to raw capacity overflow.
        // Map each to short customer-friendly copy; fall through to a
        // generic message for anything new.
        const r = result.reason;
        const friendly =
          r === "combo_line_locked"
            ? "This is part of a package, so it's priced across the whole bundle — remove the package to change it."
            : r === "capacity_overflow"
            ? "That slot is already full — try a different time or smaller party."
            : r === "rooms_sold_out"
              ? "That room type is fully booked for those dates — pick different dates."
            : r === "unit_sold_out" || r === "resource_unavailable"
              ? "That option is fully booked for the time you picked — try another time."
            : r === "seating_area_full"
              ? "That seating area is fully booked for the time you picked — choose another area or time."
            : r === "staff_only_area"
              ? "That seating area is booked by the venue team only — choose another area."
            : r === "not_pending"
              ? "Hold has expired — please remove and re-add this booking."
              : r === "outside_operating_hours"
                ? "That time is outside the service's operating hours."
                : r === "oversize_party_blocked"
                  ? "Party size is over the limit for this service — please get in touch with the venue directly."
                  : r === "oversize_party_enquiry"
                    ? "Party size needs a private enquiry — open the Enquire tab to request a quote."
                  : r === "party_below_minimum"
                    ? describeReason(result)
                    : r === "blackout_dow" || r === "blackout_range" || r === "blackout_date"
                      ? "The venue is closed at that time."
                      : r === "min_lead_hours" || r === "min_lead_minutes"
                        ? "That's too close to start time — pick a slot further out."
                        : r === "min_stay" || r === "max_stay"
                          ? "Stay length doesn't fit the venue's rules for that slot."
                          : describeEditRefusal(result);
        setEditError(friendly);
        return;
      }
      patchMetadata(item.id, {
        starts_at: result.starts_at,
        ends_at: result.ends_at,
        party_size: result.party_size,
        // update_booking_hold now re-prices the hold for the new party exactly
        // as create does. Mirror the server's amount / policy onto the line so
        // the payment chip + pay-now total stay in step with what's charged.
        // (Optional fields — a pre-migration RPC omits them, leaving the amount
        // as it was.)
        ...(typeof result.amount_cents === "number"
          ? { amount_cents: result.amount_cents }
          : {}),
        ...(typeof result.requires_full_payment === "boolean"
          ? { requires_full_payment: result.requires_full_payment }
          : {}),
      });
      // Per-unit services (default): party size is the guest count (capacity)
      // and the billed quantity is separate, so the line total (unitPrice ×
      // quantity) stays put. Per-person services bill by head, so mirror the
      // new party onto the priced quantity and the line total moves with it —
      // matching the re-priced server amount above.
      if (perPerson) {
        updateQty(item.id, result.party_size);
      }
      // A new time or length can select another duration/seasonal price.
      // Quote the normalized held window, never the caller's old duration.
      if (typeof result.unit_price_cents === "number") {
        updatePrice(item.id, result.unit_price_cents / 100);
      } else if (item.unitId && item.unitPrice != null) {
        const quote = await fetchUnitWindowPrice(item.unitId, result.starts_at, result.ends_at, result.party_size);
        if (quote) updatePrice(item.id, quote[1]);
      }
    } finally {
      setEditing(false);
    }
  }

  // Datetime edit — preserve duration so the customer just shifts the
  // window, doesn't have to recalculate the end time.
  function onChangeStarts(nextLocal: string) {
    if (!startsAt || !endsAt) return;
    const oldStarts = new Date(startsAt).getTime();
    const oldEnds = new Date(endsAt).getTime();
    const durationMs = oldEnds - oldStarts;
    // A venue-zone line's field reads the venue clock: so does its answer.
    const newStartsDate = venueZone ? new Date(zonedInputMs(nextLocal, venueZone)) : new Date(nextLocal);
    if (Number.isNaN(newStartsDate.getTime())) return;
    const newEnds = new Date(newStartsDate.getTime() + durationMs);
    applyEdit({
      starts_at: newStartsDate.toISOString(),
      ends_at: newEnds.toISOString(),
    });
  }

  function nudgeParty(delta: number) {
    if (partySize == null) return;
    const next = Math.max(guestsMin, Math.min(guestsMax, partySize + delta));
    if (next === partySize) return;
    applyEdit({ party_size: next });
  }

  // Accommodation is booked as a check-in → check-out date range (nights), so
  // the cart edits the two dates independently rather than shifting a fixed
  // duration. Each edit keeps the original local time-of-day (check-in/out
  // time) and just swaps the calendar date. (isAccommodation defined above.)
  const stayNights =
    startsAt && endsAt ? nightsBetween(startsAt, endsAt) : null;

  function onChangeCheckIn(dateStr: string) {
    if (!startsAt || !dateStr) return;
    applyEdit({ starts_at: withLocalDate(startsAt, dateStr) });
  }
  function onChangeCheckOut(dateStr: string) {
    if (!endsAt || !dateStr) return;
    applyEdit({ ends_at: withLocalDate(endsAt, dateStr) });
  }

  // Payment-policy chip — three states:
  //   full    → requires full payment OR amount equals the line total
  //   deposit → amount > 0 but less than line total
  //   none    → no charge or unknown amount
  // Cohost-paired siblings override the chain: the server zeroes their
  // charge (bundled = included in the ticket price; free_reserve = free
  // to reserve, settle on arrival), so the normal amount-driven chip
  // would lie about what's paid now.
  let policyChip: { className: string; label: string } | null = null;
  if (cohostMode === "bundled" || cohostMode === "free_reserve") {
    policyChip = {
      className: "vt-cart-item-chip-policy--none",
      label:
        cohostMode === "bundled"
          ? "Included with your event ticket"
          : "No charge to reserve — pay on arrival",
    };
  } else if (md.source === "chidesk") {
    // ChiDesk (spa): amount_cents is the PER-TREATMENT price — the balance
    // settles at the venue, so it must never drive the "Pay X now" / deposit
    // chip (with one guest it claimed the full price was due now while the
    // charge was actually the deposit). Only deposit_cents (already scaled by
    // party) is charged at checkout — mirror computeCartTotals' split.
    if (depositCents != null && depositCents > 0) {
      const balanceCents =
        lineTotalCents != null ? Math.max(0, lineTotalCents - depositCents) : null;
      policyChip =
        balanceCents != null && balanceCents > 0
          ? {
              className: "vt-cart-item-chip-policy--deposit",
              label: `${item.currency} ${(depositCents / 100).toLocaleString()} deposit · ${item.currency} ${(balanceCents / 100).toLocaleString()} on the day`,
            }
          : {
              className: "vt-cart-item-chip-policy--full",
              label: `Pay ${item.currency} ${(depositCents / 100).toLocaleString()} now`,
            };
    } else {
      policyChip = {
        className: "vt-cart-item-chip-policy--none",
        label: "No charge to reserve — settle at the venue",
      };
    }
  } else if (requiresFull || (amountCents != null && amountCents > 0 && (lineTotalCents == null || amountCents >= lineTotalCents - 1))) {
    policyChip = {
      className: "vt-cart-item-chip-policy--full",
      label: amountCents && !linkPreview
        ? `Pay ${item.currency} ${(amountCents / 100).toLocaleString()} now`
        : "Pay in full",
    };
  } else if (amountCents != null && amountCents > 0 && lineTotalCents != null) {
    // Add-ons inside amount_cents are fully paid extras, not deposit money —
    // exclude them so the deposit figure and the on-the-day balance stay true
    // (lineTotal is the service's own gross and never includes add-ons).
    const lineAddonCents =
      typeof md.addons_cents === "number" ? md.addons_cents : 0;
    const depositPortion = Math.max(0, amountCents - lineAddonCents);
    const balanceCents = Math.max(0, lineTotalCents - depositPortion);
    policyChip = {
      className: "vt-cart-item-chip-policy--deposit",
      label: `${item.currency} ${(depositPortion / 100).toLocaleString()} deposit · ${item.currency} ${(balanceCents / 100).toLocaleString()} on the day`,
    };
  } else if (depositCents != null && depositCents > 0) {
    // A separately-tracked deposit (ChiDesk spa etc.) — due now to reserve,
    // the treatment balance is settled at the venue. Without this the line
    // would fall through to "No charge to reserve", hiding a real charge.
    policyChip = {
      className: "vt-cart-item-chip-policy--deposit",
      label: `${item.currency} ${(depositCents / 100).toLocaleString()} deposit · balance at the venue`,
    };
  } else if (item.unitPrice == null || amountCents === 0) {
    policyChip = {
      className: "vt-cart-item-chip-policy--none",
      label: "No charge to reserve",
    };
  }

  const chips: React.ReactNode[] = [];

  if (startsAt && !grouped) {
    if (isEditable && isAccommodation && endsAt) {
      // Two date pickers: check-in and check-out (nights).
      chips.push(
        <label
          key="checkin"
          className="vt-cart-item-chip vt-cart-item-chip-edit"
        >
          <span className="vt-cart-item-chip-edit-label">Check-in</span>
          <input
            type="date"
            value={localDateValue(startsAt)}
            disabled={editing}
            onChange={(e) => onChangeCheckIn(e.target.value)}
          />
        </label>,
        <label
          key="checkout"
          className="vt-cart-item-chip vt-cart-item-chip-edit"
        >
          <span className="vt-cart-item-chip-edit-label">Check-out</span>
          <input
            type="date"
            value={localDateValue(endsAt)}
            min={localDateValue(startsAt)}
            disabled={editing}
            onChange={(e) => onChangeCheckOut(e.target.value)}
          />
        </label>,
      );
      if (stayNights != null) {
        chips.push(
          <span key="nights" className="vt-cart-item-chip">
            {stayNights} night{stayNights === 1 ? "" : "s"}
          </span>,
        );
      }
    } else if (isEditable) {
      chips.push(
        <label
          key="time"
          className="vt-cart-item-chip vt-cart-item-chip-edit"
        >
          <span className="vt-cart-item-chip-edit-label">When</span>
          <input
            type="datetime-local"
            value={venueZone ? zonedInputValue(startsAt, venueZone) : toLocalInputValue(startsAt)}
            disabled={editing}
            onChange={(e) => onChangeStarts(e.target.value)}
          />
        </label>,
      );
    } else {
      chips.push(
        <span key="time" className="vt-cart-item-chip">
          {formatChipDateTime(startsAt, isChideskItem(item), chipTzOffset(item.metadata), venueZone)}
        </span>,
      );
    }
  }

  if (partySize && !grouped) {
    if (isEditable) {
      chips.push(
        <span
          key="party"
          className="vt-cart-item-chip vt-cart-item-chip-stepper"
        >
          <span className="vt-cart-item-chip-edit-label">Guests</span>
          <button
            type="button"
            onClick={() => nudgeParty(-1)}
            disabled={editing || partySize <= guestsMin}
            aria-label="Decrease party size"
          >
            −
          </button>
          <span className="vt-cart-item-chip-stepper-value">{partySize}</span>
          <button
            type="button"
            onClick={() => nudgeParty(1)}
            disabled={editing || partySize >= guestsMax}
            aria-label="Increase party size"
          >
            +
          </button>
        </span>,
      );
    } else {
      chips.push(
        <span key="party" className="vt-cart-item-chip">
          {partySize} guest{partySize > 1 ? "s" : ""}
        </span>,
      );
    }
  }

  // Availability-panel lines carry their own length. A grouped visit shows
  // its shared start on the header and hides each line's When chip, so
  // without this a 90-minute ride and a 2-hour ride read as the same window.
  // Only lines the panel added (option_led) — every other line is unchanged.
  if (md.option_led === true) {
    const s = startsAt ? Date.parse(startsAt) : Number.NaN;
    const e = endsAt ? Date.parse(endsAt) : Number.NaN;
    const minutes =
      typeof md.session_minutes === "number" && md.session_minutes > 0
        ? md.session_minutes
        : Number.isFinite(s) && Number.isFinite(e) && e > s
          ? Math.round((e - s) / 60_000)
          : null;
    if (minutes != null) {
      chips.push(
        <span key="length" className="vt-cart-item-chip">{formatLength(minutes)}</span>,
      );
    }
  }
  if (seatLabel) chips.push(
    <span key="seat" className="vt-cart-item-chip">{seatLabel}</span>,
  );
  if (specialName) chips.push(
    <span key="special" className="vt-cart-item-chip vt-cart-item-chip-special">{specialName}</span>,
  );
  if (policyChip) chips.push(
    <span
      key="policy"
      className={`vt-cart-item-chip vt-cart-item-chip-policy ${policyChip.className}`}
    >
      {policyChip.label}
    </span>,
  );

  if (chips.length === 0 && !editError && !atRoomCapacity) return null;
  return (
    <>
      <div className="vt-cart-item-tags">{chips}</div>
      {atRoomCapacity && isEditable && (
        <div className="vt-cart-item-hint">
          {item.unitName ?? "This room"} sleeps up to {roomCapacity} — add it
          again for another room.
        </div>
      )}
      {losingOffer && (
        <div className="vt-cart-item-warning" role="alert">
          <span>
            That change means you lose <strong>{losingOffer.name}</strong>
            {losingOffer.cents > 0 && (
              <>
                {" "}— {item.currency}{" "}
                {(losingOffer.cents / 100).toLocaleString()} off
              </>
            )}
            .{" "}
            <button
              type="button"
              className="vt-cart-item-confirm-btn"
              disabled={editing}
              onClick={() => {
                // The guest has been told and chosen. Nothing has changed yet
                // — the preview did its work inside a transaction and rolled
                // it back — so this replays the same edit, confirmed.
                const patch = losingOffer.patch;
                setComboEditConfirmed(true);
                setLosingOffer(null);
                void applyEdit(patch);
              }}
            >
              Change it anyway
            </button>
            <button
              type="button"
              className="vt-cart-item-cancel-btn"
              onClick={() => setLosingOffer(null)}
            >
              Keep the offer
            </button>
          </span>
        </div>
      )}
      {editError && (
        <div className="vt-cart-item-warning" role="alert">
          {/* No literal ⚠ — the class supplies one via ::before. */}
          <span>{editError}</span>
        </div>
      )}
    </>
  );
}

// An edit the server refused leaves the line exactly as it was (still held).
// describeReason's catch-all is written for CREATING a booking ("Couldn't
// complete the booking (…)"), which reads as if the held booking failed —
// so a reason it does not know keeps the edit's own wording.
function describeEditRefusal(result: { reason: string; [k: string]: unknown }): string {
  // The cart has no menu chooser: a line moved onto a date where every guest
  // picks a menu has to be added again from the service.
  if (result.reason === "menu_selection_required") {
    return describeReason("menu_hold_requires_new_hold");
  }
  const described = describeReason(result);
  return described === `Couldn't complete the booking (${result.reason}).`
    ? `Couldn't update: ${result.reason}`
    : described;
}

/**
 * Converts an ISO timestamp into the `YYYY-MM-DDTHH:MM` form that
 * <input type="datetime-local"> expects in local time. Browsers won't
 * accept ISO with seconds or timezone in this control.
 */
function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// Local YYYY-MM-DD for an <input type="date">.
function localDateValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Swap the calendar date of `iso` for `dateStr` (YYYY-MM-DD, local), keeping the
// original local time-of-day (the check-in/out time). Returns a UTC ISO string.
function withLocalDate(iso: string, dateStr: string): string {
  const d = new Date(iso);
  const [y, m, day] = dateStr.split("-").map(Number);
  if (!y || !m || !day || Number.isNaN(d.getTime())) return iso;
  d.setFullYear(y, m - 1, day);
  return d.toISOString();
}

// Whole nights between two ISO timestamps (rounded), min 1.
function nightsBetween(startIso: string, endIso: string): number {
  const ms = Date.parse(endIso) - Date.parse(startIso);
  if (!Number.isFinite(ms)) return 1;
  return Math.max(1, Math.round(ms / 86_400_000));
}

/**
 * Convert ChiDesk's UTC timestamps to the tenant's wall-clock components,
 * with day rollover. The offset comes from metadata.tz_offset_minutes
 * (SAST fallback 120), so display does not depend on the browser's timezone.
 */
function wallClockParts(
  iso: string,
  offsetMin = 0,
): { date: Date; hh: string; mm: string } | null {
  const m = /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  const total = Number(m[4]) * 60 + Number(m[5]) + offsetMin;
  const dayShift = Math.floor(total / 1440);
  const mins = ((total % 1440) + 1440) % 1440;
  const pad = (n: number) => String(n).padStart(2, "0");
  // Local-midnight Date is tz-safe for the weekday/day/month label.
  return {
    date: new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + dayShift),
    hh: pad(Math.floor(mins / 60)),
    mm: pad(mins % 60),
  };
}

/** The item's UTC→tenant-local display offset (ChiDesk items; SAST fallback). */
function chipTzOffset(metadata: Record<string, unknown> | null | undefined): number {
  const v = (metadata as { tz_offset_minutes?: unknown } | null)?.tz_offset_minutes;
  return typeof v === "number" && Number.isFinite(v) ? v : 120;
}

function formatChipDateTime(iso: string, wallClock = false, offsetMin = 0, zone?: string): string {
  if (wallClock) {
    const p = wallClockParts(iso, offsetMin);
    if (p) {
      const day = p.date.toLocaleDateString("en-ZA", {
        day: "numeric",
        month: "short",
      });
      return `${day}, ${p.hh}:${p.mm}`;
    }
  }
  const d = new Date(iso);
  return d.toLocaleString("en-ZA", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    ...(zone ? { timeZone: zone } : {}),
  });
}

/**
 * Computes "Pay now" vs "Then at the venue" totals + the CTA label
 * that matches them. payNow includes any item where amount_cents > 0
 * (deposits or full prepays). payLater is line-total minus amount for
 * deposit items + line-total for reservation items (unitPrice null).
 */

function computeCartTotals(
  items: CartItem[],
  cohostPairing?: CohostPairing,
): {
  payNowCents: number;
  payLaterCents: number;
  hasReservation: boolean;
  payNowLabel: string;
  ctaLabel: string;
} {
  let payNow = 0;
  let payLater = 0;
  let hasReservation = false;
  for (const item of items) {
    const md = item.metadata ?? {};
    // Cohost sibling under bundled/free_reserve: the server zeroes its
    // amount before summing the charge, so it must not inflate pay-now
    // (or pay-later — bundled is covered by the ticket price; a
    // free_reserve table just means a bill at the venue).
    const cohostMode = cohostPairing?.siblingModes.get(item.id);
    if (cohostMode === "bundled" || cohostMode === "free_reserve") {
      if (cohostMode === "free_reserve") hasReservation = true;
      continue;
    }
    const amount = typeof md.amount_cents === "number" ? md.amount_cents : null;
    const deposit =
      typeof md.deposit_cents === "number" ? md.deposit_cents : null;
    const lineCents = lineGrossCents(item);

    // ChiDesk (spa): the treatment price settles at the venue — amount_cents is
    // the treatment value, NOT a prepay, so it must never land in pay-now. Only
    // a configured deposit is due now; the rest is "at the venue".
    if (md.source === "chidesk") {
      if (deposit != null && deposit > 0) {
        payNow += deposit;
        const gross = amount != null ? amount * item.quantity : lineCents ?? 0;
        if (gross > deposit) payLater += gross - deposit;
      } else {
        hasReservation = true;
      }
      continue;
    }

    if (item.unitPrice == null) {
      hasReservation = true;
      // A reservation item can still carry a standalone deposit (rare for
      // Vertu items, but keep pay-now honest if one is set).
      if (deposit != null && deposit > 0) payNow += deposit;
      continue;
    }
    if (amount != null && amount > 0) {
      payNow += amount;
      if (lineCents != null && lineCents > amount) {
        payLater += lineCents - amount;
      }
    } else if (lineCents != null) {
      payNow += lineCents;
    }
  }
  let payNowLabel = "Total";
  if (payNow > 0 && payLater > 0) payNowLabel = "Pay now";
  else if (payNow === 0 && hasReservation) payNowLabel = "Pay";
  let ctaLabel = "Continue to checkout";
  if (payNow === 0 && hasReservation) ctaLabel = "Confirm reservation";
  return { payNowCents: payNow, payLaterCents: payLater, hasReservation, payNowLabel, ctaLabel };
}

/**
 * Returns a map of item-id → name of the other item it overlaps
 * with. Overlap is half-open interval intersection on [starts_at,
 * ends_at), so a spa treatment at 18:30 overlaps a dinner running
 * 18:00–20:00 (even though their start times differ). Used to
 * render an inline soft-warn under each conflicting cart row.
 *
 * Delegates the heavy lifting to `state/conflicts.ts` so the same
 * logic can be reused by service tabs (cart-aware slot picking).
 */
function computeTimeConflicts(items: CartItem[]): Map<string, string> {
  const out = new Map<string, string>();
  // Spa (ChiDesk) treatments can't be concurrent for one guest — one person
  // can't be in two treatments at once — so the "same-service, same-slot =
  // group split" exception below must NOT apply to them.
  const spaItem = new Map(
    items.map((it) => [it.id, it.metadata?.source === "chidesk"]),
  );
  const intervals = getBusyIntervals(items);
  for (let i = 0; i < intervals.length; i++) {
    const a = intervals[i]!;
    // Compare against every other item EXCEPT ones from the same event — a
    // ticket and its add-ons (e.g. "General" + "Platter") share one time
    // window by design and must never be flagged as overlapping each other.
    const others = intervals.filter(
      (x, idx) =>
        idx !== i &&
        // A ticket + its add-ons share one window by design.
        !(a.eventId != null && x.eventId === a.eventId) &&
        // Same-service, same-slot lines are one seating a group split across
        // different products (e.g. two tasting flights at 11:00) — concurrent,
        // not "in two places at once". EXCEPT two spa treatments: a single guest
        // can't do both at once, so those DO get flagged (they may be for
        // different people — a soft warning, not a block).
        !(
          a.serviceId != null &&
          x.serviceId === a.serviceId &&
          x.start === a.start &&
          !spaItem.get(a.itemId) &&
          !spaItem.get(x.itemId)
        ),
    );
    const collision = findOverlap(others, a.start, a.end);
    if (collision) out.set(a.itemId, collision.description);
  }
  return out;
}

function CheckoutView({
  tenant,
  items,
  subtotal,
  currency,
  paged = false,
  notice: resumedNotice = null,
  resumed = null,
  reopened = null,
  onBack,
  onChooseAnotherTime,
  onConfirm,
}: {
  tenant: WidgetTenant;
  items: CartItem[];
  subtotal: number;
  currency: string;
  /** A phone with per-guest questions: the guests' cards one at a time (checkoutLayout's oneRiderPerScreen). */
  paged?: boolean;
  /** Reopened after a trip to Paystack: why ("We haven't received your payment…"). */
  notice?: string | null;
  /** The reopened checkout: its notice once the codes it asks for are back on (noticeNow). */
  resumed?: ResumedCheckout | null;
  /** Reopened after a trip to Paystack: what the guest gave, filled back in. */
  reopened?: ReopenedFields | null;
  onBack: () => void;
  /** After a refund with no booking: a fresh cart, back to the services. */
  onChooseAnotherTime: () => void;
  onConfirm: (payload?: CheckoutConfirmPayload) => void;
}) {
  const checkout = useCheckoutSubmit({
    tenant,
    items,
    currency,
    onConfirm: (payload) => {
      clearCheckoutResume(tenant.slug);
      onConfirm(payload);
    },
    reopened,
    // The drawer keeps its checkout only as the browser leaves for Paystack
    // (the full page keeps it all along): the cart token, the booking ids and
    // the lines, reopening on Review, never an answer or the booker's
    // contact. A guest Paystack did not charge then comes back to Pay
    // (useCheckoutReturn), not to an empty cart.
    onPaystackHop: (hop) =>
      saveCheckoutResume(tenant.slug, {
        cartToken: getCartToken(),
        items,
        step: "review",
        riderIndex: 0,
        payment: hopPayment(hop),
        codesApplied: hop.codesApplied,
        codeKinds: hop.codeKinds,
        linkCodeOnly: hop.linkCodeOnly,
        marketing: hop.marketing,
        given: hop.given,
      }),
  });
  // The hold ended: the checkout kept for the trip to Paystack is
  // forgotten, as the full page forgets its own, so nothing opens by itself
  // on a later page.
  const ended = checkout.holdEnded;
  useEffect(() => {
    if (ended) clearCheckoutResume(tenant.slug);
  }, [ended, tenant.slug]);
  // A guard notice (GuardNotice) replaces it rather than sitting beside it.
  const [notice, setNotice] = useState<string | null>(resumedNotice);
  useEffect(() => {
    if (checkout.guard) setNotice(null);
  }, [checkout.guard]);
  // Without "apply your code again" once the codes are back on (a campaign
  // link's code is applied again by itself).
  const shownNotice = noticeNow(notice, resumed, checkout.appliedCodes, checkout.linkCode);
  // Reopened with a notice ("We haven't received your payment…"): it takes
  // the focus once, so a screen reader says why the checkout is back (a
  // status inserted with its words already in is often not announced).
  const noticeRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    if (resumedNotice) noticeRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    firstName,
    lastName,
    email,
    phone,
    participantSignature,
    setParticipantsValid,
    participantHandle,
    error,
    checkoutFormRef,
    holdEnded,
    setHoldEnded,
    paymentLapsed,
    markLapsed,
    isMixedCart,
    submit,
    submitLabel,
  } = checkout;

  return (
    <form ref={checkoutFormRef} className="vt-checkout vt-cart-checkout" onSubmit={submit}>
      {/* The hold timer: the time left in the last five minutes, and more
          time asked for while the guest is typing (HoldTimer). */}
      <HoldTimer items={items} formRef={checkoutFormRef} onBack={onBack} onEndedChange={setHoldEnded} />
      {/* data-hold-passive: tapping it is the guest leaving, not activity.
          Once the hold has ended the sticky banner carries this action, so
          it is not shown twice. */}
      {!holdEnded && (
        <button type="button" className="vt-link" data-hold-passive onClick={onBack}>
          ← Back to your visit
        </button>
      )}
      {shownNotice && (
        <p className="vt-checkout-page-notice vt-checkout-drawer-notice" role="status" tabIndex={-1} ref={noticeRef}>
          {shownNotice}
        </p>
      )}

      {isMixedCart && (
        <p className="vt-checkout-note">
          You&apos;re booking spa treatments alongside your other items —
          we&apos;ll take one payment now (your bookings in full plus the spa
          deposit); the spa treatment balance is settled at the venue.
        </p>
      )}

      <div className="vt-checkout-section-title">Your details</div>
      <ContactFields c={checkout} />
      <SpecialRequestsField c={checkout} />

      {participantSignature && (
        <ParticipantDetails
          cartToken={getCartToken()}
          signature={participantSignature}
          onValidityChange={setParticipantsValid}
          handleRef={participantHandle}
          booker={{ firstName, lastName, email, phone }}
          venueName={tenant.name}
          paged={paged}
          // On a phone each rider is saved as the guest pages through them:
          // a hold that ended stops those saves, and one a save finds ended stops Pay.
          hold={{ ended: holdEnded, lapsed: paymentLapsed, onLapse: markLapsed, onBack }}
          // What the payment button is called in the pager's words ("tap Pay to try again").
          payName={checkout.isNetZeroVertu ? "Confirm booking" : "Pay"}
        />
      )}

      <ChideskAttribution c={checkout} />

      <CheckoutTotals c={checkout} currency={currency} />

      <CheckoutConsents c={checkout} />

      <CohostNotice c={checkout} />

      {error && <div className="vt-capacity-error">{error}</div>}
      <GuardNotice c={checkout} onChooseAnotherTime={onChooseAnotherTime} />

      {!payBlockedByGuard(checkout.guard) && !offersBookAnother(checkout.guard) && (
        <button
          type="submit"
          className="vt-cta"
          disabled={payBlocked(checkout)}
        >
          {submitLabel}
        </button>
      )}
      <PayNotes c={checkout} />
    </form>
  );
}

/**
 * Back from Paystack while the payment is not confirmed yet. "Confirming your
 * payment…" while the server and Paystack are asked, then what Paystack said
 * (state/payment-return.ts decides the words and what is offered):
 *   received  "Payment received — we're confirming your booking…" until the
 *             webhook's confirmation is on file; Check again only once that
 *             wait is over. Nothing that leads to Pay.
 *   going     "Your payment is still going through." with Check again.
 *             Nothing that leads to Pay.
 *   not_paid  the kept checkout could not reopen by itself: why, and Review
 *             & pay to try again when it is still kept.
 *   refunded  the payment is being refunded: with no booking (the reader
 *             says so), that it could not be kept and Choose another time;
 *             with the booking standing, that it is confirmed; on Paystack's
 *             word alone, where to look. Nothing to pay.
 *   still     no answer to trust: where the confirmation will arrive, Check
 *             again, and — when this tab kept the checkout — Back to your
 *             checkout beside "check there before booking again".
 *   superseded  paid on a page opened before the booking changed: this
 *             payment goes back in full; while the booking is held, "Pay
 *             R1,500" opens the venue's pay page for it (the cart as it
 *             stands now); with no booking held, Choose another time
 *             (20261003200010).
 * Never a silent drawer. Each new outcome takes the focus (its words, so a
 * screen reader reads them and Tab starts inside the drawer); a button that
 * is busy stays focusable (aria-disabled), so pressing it never drops the
 * focus to the page.
 */
function PaymentReturnView({
  state,
  onClose,
  onChooseAnotherTime,
  timeZone,
}: {
  state: PaymentReturnState | null;
  onClose: () => void;
  onChooseAnotherTime: () => void;
  /** The venue's time zone (how long the booking is still held). */
  timeZone?: string;
}) {
  const phase: ReturnPhase = state?.phase ?? "confirming";
  const late = state?.late === true;
  const refund = state?.refund ?? null;
  const inFull = state?.refundInFull !== false;
  const kept = Boolean(state?.onBackToCheckout || state?.onReviewAndPay);
  // Only what the phase allows is ever drawn, whatever handlers arrive.
  const offer = returnOffer(phase, { kept, late });
  const chooseAnother = offersChooseAnotherTime(phase, refund);
  const hint = returnHint(phase, { late, kept, problem: Boolean(state?.problem), refund });
  const busy = state?.backBusy === true || state?.checking === true;
  const answered = phase !== "confirming";
  const statusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    statusRef.current?.focus({ preventScroll: true });
  }, [phase, late, refund]);
  // A press while busy does nothing (the button keeps the focus).
  const unlessBusy = (run?: () => void) => () => {
    if (!busy) run?.();
  };
  if (phase === "superseded" && state?.superseded) {
    const words = supersededWords(state.superseded, { timeZone });
    return (
      <div className="vt-checkout vt-cart-checkout vt-payment-return" data-phase={phase}>
        <div className="vt-payment-return-status" role="status" aria-live="polite" tabIndex={-1} ref={statusRef}>
          <p className="vt-payment-return-text">{words.message}</p>
          <p className="vt-payment-return-note">{words.hint}</p>
          {words.payElsewhere && <p className="vt-payment-return-note">{words.payElsewhere}</p>}
        </div>
        {state.problem && (
          <div className="vt-capacity-error vt-payment-return-problem" role="alert">
            {state.problem}
          </div>
        )}
        <div className="vt-payment-return-actions">
          {words.payLabel && state.onPayUpdated && (
            <button type="button" className="vt-cta" onClick={unlessBusy(state.onPayUpdated)} aria-disabled={busy || undefined}>
              {state.backBusy ? SUPERSEDED_PAY_BUSY : words.payLabel}
            </button>
          )}
          {words.chooseAnotherTime ? (
            <button type="button" className="vt-cta" onClick={onChooseAnotherTime}>
              {CHOOSE_ANOTHER_TIME}
            </button>
          ) : (
            <button type="button" className="vt-link" onClick={onClose}>
              Close and keep browsing
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div
      className="vt-checkout vt-cart-checkout vt-payment-return"
      data-phase={phase}
      data-late={late ? "" : undefined}
      data-refund={refund ?? undefined}
    >
      {/* The headline and what to do meanwhile are announced together. */}
      <div className="vt-payment-return-status" role="status" aria-live="polite" tabIndex={-1} ref={statusRef}>
        <p className="vt-payment-return-text">{returnMessage(phase, { late, refund, inFull })}</p>
        {hint && !offer.backToCheckout && <p className="vt-payment-return-note">{hint}</p>}
      </div>
      {answered && state?.problem && (
        <div className="vt-capacity-error vt-payment-return-problem" role="alert">
          {state.problem}
        </div>
      )}
      {answered && (
        <div className="vt-payment-return-actions">
          {offer.checkAgain && state?.onCheckAgain && (
            <button type="button" className="vt-cta" onClick={unlessBusy(state.onCheckAgain)} aria-disabled={busy || undefined}>
              {state.checking ? "Checking…" : "Check again"}
            </button>
          )}
          {offer.reviewAndPay && state?.onReviewAndPay && (
            <button type="button" className="vt-cta" onClick={unlessBusy(state.onReviewAndPay)} aria-disabled={busy || undefined}>
              {state.backBusy ? "Reopening your checkout…" : "Review & pay"}
            </button>
          )}
          {offer.backToCheckout && state?.onBackToCheckout && (
            <>
              {hint && <p className="vt-payment-return-hint">{hint}</p>}
              <button
                type="button"
                className="vt-payment-return-back"
                onClick={unlessBusy(state.onBackToCheckout)}
                aria-disabled={busy || undefined}
              >
                {state.backBusy ? "Reopening your checkout…" : "Back to your checkout"}
              </button>
            </>
          )}
          {/* No booking, the money going back: the way back to choose
              another time, on a fresh cart (the drawer closes onto the
              services). */}
          {chooseAnother ? (
            <button type="button" className="vt-cta" onClick={onChooseAnotherTime}>
              {CHOOSE_ANOTHER_TIME}
            </button>
          ) : (
            /* Named apart from the header's × ("Close"). */
            <button type="button" className="vt-link" onClick={onClose}>
              Close and keep browsing
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function describeSeat(
  metadata: Record<string, unknown>,
  serviceType: string | undefined,
  holdChip: (bookingId: string) => HoldChip | null,
): React.ReactNode {
  const label = metadata.seat_label as string | null | undefined;
  const plan = metadata.floor_plan_name as string | null | undefined;
  const starts = metadata.starts_at as string | null | undefined;
  const ends = metadata.ends_at as string | null | undefined;
  const party = metadata.party_size as number | null | undefined;
  const bookingId = metadata.booking_id as string | null | undefined;
  const hold = bookingId && serviceType !== "accommodation" ? holdChip(bookingId) : null;

  if (!label && !plan && !starts) return null;

  return (
    <>
      {(plan || label) && (
        <div className="vt-cart-item-detail">
          Seat: {[plan, label].filter(Boolean).join(" · ")}
        </div>
      )}
      {starts && ends && (
        <div className="vt-cart-item-detail">
          {humanWindow(starts, ends, metadata.source === "chidesk", chipTzOffset(metadata), venueZoneOf(metadata))}
          {party ? ` · ${party} guest${party === 1 ? "" : "s"}` : ""}
        </div>
      )}
      {/* "pending": the line is kept but blank until the first read answers,
          so the words never change in front of the guest. "lapsed": the
          checkout's own lapse line, announced once. */}
      {hold && (
        <div
          className={`vt-cart-item-detail vt-cart-item-hold${hold.tone === "lapsed" ? " vt-cart-item-hold-lapsed" : ""}${hold.tone === "pending" ? " vt-cart-item-hold-pending" : ""}`}
          aria-hidden={hold.tone === "pending" ? true : undefined}
          role={hold.tone === "lapsed" ? "status" : undefined}
          data-hold-chip={hold.tone}
        >
          {hold.text}
        </div>
      )}
    </>
  );
}

function humanWindow(starts: string, ends: string, wallClock = false, offsetMin = 0, zone?: string): string {
  if (wallClock) {
    const a = wallClockParts(starts, offsetMin);
    const b = wallClockParts(ends, offsetMin);
    if (a && b) {
      const sameDay = a.date.toDateString() === b.date.toDateString();
      const dayA = a.date.toLocaleDateString("en-ZA", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      if (!sameDay) {
        const dayB = b.date.toLocaleDateString("en-ZA", {
          weekday: "short",
          day: "numeric",
          month: "short",
        });
        return `${dayA} ${a.hh}:${a.mm} → ${dayB} ${b.hh}:${b.mm}`;
      }
      return `${dayA} · ${a.hh}:${a.mm}–${b.hh}:${b.mm}`;
    }
  }
  const a = new Date(starts);
  const b = new Date(ends);
  // A venue-zone line (the availability panel's) reads the venue's clock.
  const inZone = zone ? { timeZone: zone } : {};
  const sameDay = zone
    ? ymdOfInstant(a.getTime(), zone) === ymdOfInstant(b.getTime(), zone)
    : a.toDateString() === b.toDateString();
  const day = a.toLocaleDateString("en-ZA", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...inZone,
  });
  const t1 = a.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false, ...inZone });
  const t2 = b.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false, ...inZone });
  if (!sameDay) {
    const day2 = b.toLocaleDateString("en-ZA", {
      weekday: "short",
      day: "numeric",
      month: "short",
      ...inZone,
    });
    return `${day} ${t1} → ${day2} ${t2}`;
  }
  return `${day} · ${t1}–${t2}`;
}

/** "Email" → "Email". "Email", "WhatsApp" → "Email and WhatsApp". */
function formatList(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0]!;
  return items.slice(0, -1).join(", ") + " and " + items.slice(-1);
}

function ConfirmedView({
  tenant,
  confirmation,
  onClose,
}: {
  tenant: WidgetTenant;
  confirmation: {
    customerFirstName: string | null;
    customerEmail: string | null;
    customerPhone: string | null;
    bookingReference: string | null;
    amountCents: number | null;
    serverReports?: boolean;
    serviceNames?: string[];
    tickets?: ConfirmationTicket[];
    manageUrl?: string | null;
    deferredDeposit?: boolean;
    depositDueAt?: string;
    depositCents?: number;
    depositCurrency?: string;
  };
  onClose: () => void;
}) {
  // In-widget (non-Paystack) confirmations: the Google Ads conversion; GA4
  // and Meta only for a booking the server does not report (a spa booking
  // without a deposit), else the server's (trackPurchase). The Paystack-paid
  // path leaves amountCents null:
  // its Ads conversion fires on the return (WidgetShell), and its GA4 + Meta
  // purchase server-side, so no booking is counted twice.
  // The venue's own tags (an embed on its site) hear of the same booking
  // through its dataLayer; the paid paths push theirs where the paid amount
  // is read (WidgetShell, useCheckoutSubmit, ChideskResumeModal), and a
  // deposit deferred to later (amountCents null here too) is reserved, not
  // yet bought.
  useEffect(() => {
    if (confirmation.amountCents != null) {
      trackPurchase({
        reference: confirmation.bookingReference,
        value: confirmation.amountCents / 100,
        currency: tenant.currency ?? "ZAR",
        email: confirmation.customerEmail,
        serverReports: confirmation.serverReports !== false,
      });
      pushHostPurchase({
        tenant: tenant.slug,
        reference: confirmation.bookingReference,
        value: confirmation.amountCents / 100,
        currency: tenant.currency ?? "ZAR",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const greeting = confirmation.customerFirstName
    ? `All set, ${confirmation.customerFirstName}!`
    : "All set!";

  // Advance-deposit deferral: reserved now, deposit due later — different copy.
  const deferred = confirmation.deferredDeposit === true;
  const depositLabel =
    confirmation.depositCents != null
      ? `${confirmation.depositCurrency ?? tenant.currency ?? "ZAR"} ${(
          confirmation.depositCents / 100
        ).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
      : null;
  const dueLabel = confirmation.depositDueAt
    ? new Date(confirmation.depositDueAt).toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    : null;
  const reservedGreeting = confirmation.customerFirstName
    ? `Reserved, ${confirmation.customerFirstName}!`
    : "Reserved!";

  // Contact destinations for enabled tenant channels. This configuration
  // is not a delivery receipt: booking confirmation can succeed while a
  // message is still queued or cannot be sent. Do not promise a send here.
  const CHANNEL_LABEL: Record<string, string> = {
    email: "Email",
    sms: "SMS",
    whatsapp: "WhatsApp",
  };
  // Canonical channel labels (Email / SMS / WhatsApp), deduped. Tenant
  // display_names often ALL equal the venue name, which produced rows like
  // "Laborie Estate → …" twice and "via Laborie Estate and Laborie Estate" —
  // the channel TYPE is the informative part here.
  const channels: Array<{ label: string; destination: string }> = [];
  const enabled = (tenant.messaging_channels ?? []).filter((c) => c.is_enabled);
  for (const ch of enabled) {
    let destination: string | null = null;
    if (ch.channel === "email") destination = confirmation.customerEmail;
    else if (ch.channel === "sms" || ch.channel === "whatsapp") destination = confirmation.customerPhone;
    const label = CHANNEL_LABEL[ch.channel] ?? ch.channel;
    if (destination && !channels.some((c) => c.label === label)) {
      channels.push({ label, destination });
    }
  }

  // Event tickets, grouped as "2× General · Wine Club Evening" for a compact list.
  const ticketGroups = (() => {
    const map = new Map<string, { qty: number; type: string; event: string }>();
    for (const t of confirmation.tickets ?? []) {
      const key = `${t.ticket_type}||${t.event_name}`;
      const cur = map.get(key);
      if (cur) cur.qty += 1;
      else map.set(key, { qty: 1, type: t.ticket_type, event: t.event_name });
    }
    return Array.from(map.values());
  })();
  // When every ticket is for one event, the event is named once, not on each
  // row: "5× Land & Flame · Fire & Feast · Land & Flame" read as a stutter
  // (2 Oct 2026). If the line above already names that event, it is not
  // repeated at all; otherwise it heads the list.
  const firstGroup = ticketGroups[0];
  const singleEvent =
    firstGroup && ticketGroups.every((g) => g.event === firstGroup.event)
      ? firstGroup.event || null
      : null;
  const eventInHeadline =
    !!singleEvent && (confirmation.serviceNames ?? []).includes(singleEvent);

  return (
    <div className="vt-confirmed">
      <div className="vt-confirm-hero">
        <div className="vt-check">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3>{deferred ? reservedGreeting : greeting}</h3>
        {confirmation.bookingReference && (
          <>
            <div className="vt-confirm-ref-label">Booking reference</div>
            <div className="vt-confirm-ref">{confirmation.bookingReference}</div>
          </>
        )}
        {/* Lead with WHAT was booked; the venue rides along. Falls back to
            tenant-only when the service names weren't captured (Paystack
            return path). */}
        <p className="vt-muted">
          {deferred
            ? `Your spot${
                confirmation.serviceNames && confirmation.serviceNames.length > 0
                  ? ` at ${formatList(confirmation.serviceNames)}`
                  : ""
              } is held.${
                depositLabel
                  ? ` A deposit of ${depositLabel}${dueLabel ? ` is due by ${dueLabel}` : " is still due"}.`
                  : ""
              }`
            : confirmation.serviceNames && confirmation.serviceNames.length > 0
              ? `Your booking at ${formatList(confirmation.serviceNames)} is confirmed · ${tenant.name}.`
              : `Your booking is confirmed with ${tenant.name}.`}
        </p>
      </div>

      {ticketGroups.length > 0 && (
        <div className="vt-confirm-section">
          <h4>Your tickets</h4>
          {singleEvent && !eventInHeadline ? <p className="vt-muted">{singleEvent}</p> : null}
          <ul className="vt-confirm-tickets">
            {ticketGroups.map((g) => (
              <li key={`${g.type}-${g.event}`} className="vt-confirm-ticket-row">
                <strong>{g.qty}×</strong> {g.type}
                {g.event && !singleEvent ? <span className="vt-muted"> · {g.event}</span> : null}
              </li>
            ))}
          </ul>
          {confirmation.manageUrl ? (
            <a
              className="vt-confirm-tickets-cta"
              href={confirmation.manageUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View your tickets &amp; QR codes →
            </a>
          ) : (
            <p className="vt-muted">
              Contact {tenant.name} for your tickets and QR codes.
            </p>
          )}
        </div>
      )}

      {channels.length > 0 && (
        <div className="vt-confirm-section">
          <h4>Your contact details</h4>
          {channels.map((ch) => (
            <div key={ch.label} className="vt-confirm-channel">
              <span className="vt-confirm-channel-icon">→</span>
              <strong>{ch.label}</strong>
              <span className="vt-confirm-channel-dest">{ch.destination}</span>
            </div>
          ))}
        </div>
      )}

      <div className="vt-confirm-section">
        <h4>What's next</h4>
        <ul className="vt-confirm-next">
          {deferred ? (
            <>
              <li>If your deposit payment link hasn&apos;t arrived, contact {tenant.name}.</li>
              <li>Your reservation is held until the deposit deadline — paying the deposit locks it in.</li>
              <li>Keep your booking reference for any questions or changes.</li>
            </>
          ) : (
            <>
              <li>Keep your booking reference for your visit.</li>
              {!confirmation.manageUrl && (
                <li>To change or cancel your booking, contact {tenant.name}.</li>
              )}
              <li>If a confirmation message hasn&apos;t arrived, contact {tenant.name}.</li>
            </>
          )}
        </ul>
      </div>

      {confirmation.manageUrl && ticketGroups.length === 0 && (
        <a
          className="vt-confirm-tickets-cta"
          href={confirmation.manageUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Manage your booking →
        </a>
      )}

      <button type="button" className="vt-cta" onClick={onClose}>
        Done
      </button>
    </div>
  );
}

// ─── Add-on strip ("Make it special") ────────────────────────────────────────
// Shown under the cart lines: for each NATIVE service in the cart with a
// configured add-on catalogue, quantity steppers per extra. The selection is
// stamped on that service's first booking row server-side (set_booking_addons
// prices it into amount_cents), then mirrored into the cart line's metadata so
// totals and the line's sublines update instantly.
function AddonStrip({
  items,
  serviceAddons,
}: {
  items: ReturnType<typeof useCart>["items"];
  serviceAddons?: Record<string, WidgetAddon[]>;
}) {
  const { patchMetadata } = useCart();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!serviceAddons) return null;

  // Anchor line per service: the first native (non-chidesk) item holding a
  // real booking — the row the selection is stamped on.
  const anchors = new Map<string, (typeof items)[number]>();
  for (const item of items) {
    const meta = item.metadata as Record<string, unknown>;
    // chidesk: no native booking row; event: booking rows carry service_id
    // NULL so the RPC can't validate a catalogue; combo: bundle pricing owns
    // the amount. cohost-priced lines are refused server-side too.
    if (meta.source === "chidesk" || meta.source === "event" || meta.source === "combo") continue;
    if (typeof meta.booking_id !== "string") continue;
    if (!anchors.has(item.serviceId)) anchors.set(item.serviceId, item);
  }

  const sections = [...anchors.entries()].filter(
    ([serviceId]) => (serviceAddons[serviceId] ?? []).length > 0,
  );
  if (sections.length === 0) return null;

  async function change(
    anchor: (typeof items)[number],
    catalogue: WidgetAddon[],
    addonId: string,
    delta: number,
  ) {
    const current = (Array.isArray(anchor.metadata.addons)
      ? (anchor.metadata.addons as AddonSnapshotLine[])
      : []
    ).map((a) => ({ addon_id: a.addon_id, qty: a.qty }));
    const idx = current.findIndex((a) => a.addon_id === addonId);
    const max = catalogue.find((a) => a.id === addonId)?.max_qty ?? 10;
    if (idx >= 0) {
      current[idx] = {
        addon_id: addonId,
        qty: Math.max(0, Math.min(max, (current[idx]?.qty ?? 0) + delta)),
      };
    } else if (delta > 0) {
      current.push({ addon_id: addonId, qty: 1 });
    }
    const next = current.filter((a) => a.qty > 0);
    setPendingId(addonId);
    setError(null);
    const r = await setBookingAddons({
      bookingId: anchor.metadata.booking_id as string,
      cartToken: getCartToken(),
      addons: next,
    });
    setPendingId(null);
    if (!r.ok) {
      setError(
        r.reason === "checkout_started"
          ? "You've already started checkout — complete your payment first, then extras can be added at the venue."
          : "Couldn't update the extras — please try again.",
      );
      return;
    }
    patchMetadata(anchor.id, {
      addons: r.addons,
      addons_cents: r.addons_cents,
      amount_cents: r.amount_cents,
    });
  }

  return (
    <div className="vt-addon-strip">
      <div className="vt-addon-strip-title">Make it special</div>
      {sections.map(([serviceId, anchor]) => {
        const catalogue = serviceAddons[serviceId] ?? [];
        const selected = Array.isArray(anchor.metadata.addons)
          ? (anchor.metadata.addons as AddonSnapshotLine[])
          : [];
        return (
          <div key={serviceId} className="vt-addon-section">
            {sections.length > 1 && (
              <div className="vt-addon-service">{anchor.serviceName}</div>
            )}
            {catalogue.map((a) => {
              const qty = selected.find((s) => s.addon_id === a.id)?.qty ?? 0;
              return (
                <div key={a.id} className="vt-addon-row">
                  <div className="vt-addon-info">
                    <span className="vt-addon-name">{a.name}</span>
                    {a.description && (
                      <span className="vt-addon-desc">{a.description}</span>
                    )}
                  </div>
                  <span className="vt-addon-price">
                    {a.currency} {(a.price_cents / 100).toLocaleString()}
                  </span>
                  <div className="vt-addon-stepper">
                    <button
                      type="button"
                      aria-label={`Remove ${a.name}`}
                      disabled={pendingId != null || qty === 0}
                      onClick={() => void change(anchor, catalogue, a.id, -1)}
                    >
                      −
                    </button>
                    <span>{qty}</span>
                    <button
                      type="button"
                      aria-label={`Add ${a.name}`}
                      disabled={pendingId != null || qty >= a.max_qty}
                      onClick={() => void change(anchor, catalogue, a.id, 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      {error && <div className="vt-addon-error">{error}</div>}
    </div>
  );
}
